import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, DollarSign, History, Pencil } from "lucide-react";
import { toast } from "sonner";
import { usePortalAuth } from "../lib/portalAuth";
import { OW_API } from "../lib/operatorWalk";
import AdminLayout from "../components/admin/AdminLayout";
import PricingEditModal from "../components/ow/PricingEditModal";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminPricing() {
  const { authHeader } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const tab = location.pathname.endsWith("/history") ? "history" : "prices";

  const [grouped, setGrouped] = useState([]);
  const [unitPresets, setUnitPresets] = useState([]);
  const [priceTypes, setPriceTypes] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const loadPrices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${OW_API}/admin/pricing`, { headers: authHeader });
      setGrouped(res.data.grouped || []);
      setUnitPresets(res.data.unit_presets || []);
      setPriceTypes(res.data.price_types || []);
    } catch {
      toast.error("Could not load prices.");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${OW_API}/admin/pricing/history`, { headers: authHeader });
      setHistory(res.data.history || []);
    } catch {
      toast.error("Could not load change history.");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (tab === "history") loadHistory();
    else loadPrices();
  }, [tab, loadHistory, loadPrices]);

  const toggleActive = async (entry) => {
    setTogglingId(entry.id);
    setGrouped((prev) =>
      prev.map((g) => ({
        ...g,
        entries: g.entries.map((e) => (e.id === entry.id ? { ...e, is_active: !e.is_active } : e)),
      }))
    );
    try {
      await axios.patch(`${OW_API}/admin/pricing/${entry.id}`, { is_active: !entry.is_active }, { headers: authHeader });
      toast.success(entry.is_active ? "Price hidden from the site." : "Price is now live on the site.");
    } catch {
      toast.error("Could not update this price.");
      setGrouped((prev) =>
        prev.map((g) => ({
          ...g,
          entries: g.entries.map((e) => (e.id === entry.id ? { ...e, is_active: entry.is_active } : e)),
        }))
      );
    } finally {
      setTogglingId(null);
    }
  };

  const onSaved = () => {
    setEditing(null);
    loadPrices();
  };

  const tabs = (
    <div className="flex items-center gap-2" data-testid="admin-pricing-tabs">
      <button
        onClick={() => navigate("/admin/pricing")}
        className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border transition-colors ${
          tab === "prices" ? "bg-brand-700 text-white border-brand-700" : "bg-white text-brand-700 border-brand-200 hover:border-brand-500"
        }`}
        data-testid="admin-pricing-tab-prices"
      >
        <DollarSign className="w-4 h-4" /> Prices
      </button>
      <button
        onClick={() => navigate("/admin/pricing/history")}
        className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border transition-colors ${
          tab === "history" ? "bg-brand-700 text-white border-brand-700" : "bg-white text-brand-700 border-brand-200 hover:border-brand-500"
        }`}
        data-testid="admin-pricing-tab-history"
      >
        <History className="w-4 h-4" /> Change history
      </button>
    </div>
  );

  return (
    <AdminLayout title="Pricing" subtitle="Marketing prices render on the public site without a deploy." actions={tabs}>
      {loading ? (
        <div className="flex items-center justify-center py-24 text-brand-500" data-testid="admin-pricing-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : tab === "history" ? (
        <HistoryView history={history} />
      ) : (
        <div className="space-y-10" data-testid="admin-pricing-list">
          {grouped.map((group) => (
            <section key={group.service_display_name} data-testid={`pricing-group-${group.service_display_name}`}>
              <h2 className="font-serif text-2xl text-brand-700 mb-4">{group.service_display_name}</h2>
              <div className="border border-brand-200 bg-white">
                {group.entries.map((entry, i) => (
                  <div
                    key={entry.id}
                    className={`flex flex-col sm:flex-row sm:items-center gap-4 px-5 py-5 ${
                      i < group.entries.length - 1 ? "border-b border-brand-200" : ""
                    } ${entry.is_active ? "" : "bg-brand-100/50"}`}
                    data-testid={`pricing-row-${entry.service_key}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="text-sm font-medium text-brand-700">{entry.label}</span>
                        <span className="text-[10px] tracking-[0.15em] uppercase text-brand-400 border border-brand-200 px-1.5 py-0.5">{entry.price_type}</span>
                        {!entry.is_active && (
                          <span className="text-[10px] tracking-[0.15em] uppercase text-ink-muted border border-ink-line px-1.5 py-0.5">Hidden</span>
                        )}
                      </div>
                      <div className="font-serif text-xl text-brand-700 mt-1.5">{entry.display}</div>
                      <div className="text-xs text-ink-muted mt-1">
                        {entry.last_updated_by ? `Updated by ${entry.last_updated_by} ` : ""}
                        {entry.last_updated_at ? formatWhen(entry.last_updated_at) : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        onClick={() => toggleActive(entry)}
                        disabled={togglingId === entry.id}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 ${
                          entry.is_active ? "bg-brand-700" : "bg-brand-200"
                        }`}
                        title={entry.is_active ? "Active on site" : "Hidden from site"}
                        data-testid={`pricing-toggle-${entry.service_key}`}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${entry.is_active ? "translate-x-5" : "translate-x-0.5"}`} />
                      </button>
                      <button
                        onClick={() => setEditing(entry)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-brand-700 border border-brand-200 hover:border-brand-500 transition-colors"
                        data-testid={`pricing-edit-${entry.service_key}`}
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <PricingEditModal
          entry={editing}
          authHeader={authHeader}
          unitPresets={unitPresets}
          priceTypes={priceTypes}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </AdminLayout>
  );
}

function HistoryView({ history }) {
  if (history.length === 0) {
    return (
      <div className="bg-white border border-brand-200 px-6 py-16 text-center text-ink-muted" data-testid="admin-pricing-history-empty">
        No price changes have been recorded yet.
      </div>
    );
  }
  return (
    <div className="border border-brand-200 bg-white" data-testid="admin-pricing-history-list">
      {history.map((h, i) => (
        <div
          key={h.id}
          className={`px-5 py-5 ${i < history.length - 1 ? "border-b border-brand-200" : ""}`}
          data-testid={`pricing-history-row-${i}`}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm font-medium text-brand-700">
              {h.service_display_name} <span className="text-ink-muted">/ {h.label}</span>
            </div>
            <div className="text-xs text-ink-muted">{formatWhen(h.changed_at)}{h.changed_by ? ` by ${h.changed_by}` : ""}</div>
          </div>
          <div className="mt-2 flex items-center gap-3 text-sm flex-wrap">
            <span className="text-ink-muted line-through">{h.previous_display}</span>
            <span className="text-brand-400">to</span>
            <span className="font-serif text-lg text-brand-700">{h.new_display}</span>
          </div>
          {h.previous_active !== h.new_active && (
            <div className="mt-1.5 text-xs text-brand-500">
              {h.new_active ? "Made active on the site." : "Hidden from the site."}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
